import React, { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Search, ShoppingBag, Coffee, FileCheck } from "lucide-react";
import { Input } from "../ui/input";

function SampleStrategyModal({ isOpen, onClose, onSelect }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredStrategies, setFilteredStrategies] = useState([]);

  // Sample strategies
  const strategies = [
    {
      id: "kayyem-stores",
      name: "KAY YEM Stores Strategy",
      description:
        "Parse invoices from KAY YEM Stores with item extraction, totals, and discounts.",
      icon: <ShoppingBag className="h-10 w-10 text-blue-500" />,
      code: `using DigiReceiptOcr.Models;
using System;
using System.Linq;
using System.Text.RegularExpressions;

namespace DigiReceiptOcr.Strategies
{
    public class KAYYEMStoresParsingStrategy : IInvoiceParsingStrategy
    {
        public void ExtractInvoiceDetails(string formattedText, ref Invoice invoice, InvoiceTemplateConfig config)
        {
            ExtractInvoiceNumber(formattedText, ref invoice);
            ExtractShopName(formattedText, ref invoice);
            ExtractShopAddress(formattedText, ref invoice);
            ExtractShopMobileNumber(formattedText, ref invoice);
            ExtractItems(formattedText, ref invoice);
            ExtractGrandTotal(formattedText, ref invoice);
            ExtractSubTotal(formattedText, ref invoice);
            ExtractTotalDiscount(formattedText, ref invoice);
            ExtractPurchaseDate(formattedText, ref invoice);
            ExtractPurchaseTime(formattedText, ref invoice);
            CalculateItemDiscounts(ref invoice);
        }

        private static void ExtractInvoiceNumber(string formattedText, ref Invoice invoice)
        {
            var match = Regex.Match(formattedText, @"InvNo\s*:\s*(\d+)");
            if (match.Success)
            {
                invoice.InvoiceNo = match.Groups[1].Value;
            }
        }

        private static void ExtractShopName(string formattedText, ref Invoice invoice)
        {
            var lines = formattedText.Split('\\n');
            if (lines.Length > 0)
            {
                invoice.ShopName = lines[0].Trim();
            }
        }

        private static void ExtractShopAddress(string formattedText, ref Invoice invoice)
        {
            var addressRegex = new Regex(@"KAY YEM STORES\\n\\n(.*?)GSTIN:", RegexOptions.Singleline);
            var match = addressRegex.Match(formattedText);
            if (match.Success)
            {
                var address = match.Groups[1].Value
                    .Replace("\\n", " ")
                    .Replace(".PH", ", PH")
                    .Replace(".PIN", ", PIN")
                    .Trim();
                invoice.ShopAddress = address;
            }
        }

        private static void ExtractShopMobileNumber(string formattedText, ref Invoice invoice)
        {
            var phoneMatch = Regex.Match(formattedText, @"PH[\.:](\d{10})");
            if (phoneMatch.Success)
            {
                invoice.ShopMobileNumber = phoneMatch.Groups[1].Value;
            }
        }

        private static void ExtractItems(string formattedText, ref Invoice invoice)
        {
            // Implementation for item extraction...
            // This is a placeholder - the real implementation would be much more detailed
            invoice.ItemsList = new List<Item>();
        }

        private static void ExtractGrandTotal(string formattedText, ref Invoice invoice)
        {
            var totalMatch = Regex.Match(formattedText, @"BILL AMOUNT\s*:\s*(\d+\.\d{2})");
            if (totalMatch.Success)
            {
                invoice.GrandTotal = decimal.Parse(totalMatch.Groups[1].Value);
            }
        }

        private static void ExtractSubTotal(string formattedText, ref Invoice invoice)
        {
            var netTotalMatch = Regex.Match(formattedText, @"Net Total\s*:\s*(\d+\.\d{2})");
            if (netTotalMatch.Success)
            {
                invoice.SubTotal = decimal.Parse(netTotalMatch.Groups[1].Value);
            }
        }

        private static void ExtractTotalDiscount(string formattedText, ref Invoice invoice)
        {
            var discountMatch = Regex.Match(formattedText, @"YOU HAVE SAVED Rs\.\s*(\d+\.\d{2})");
            if (discountMatch.Success)
            {
                invoice.TotalDiscount = decimal.Parse(discountMatch.Groups[1].Value);
            }
        }

        private static void CalculateItemDiscounts(ref Invoice invoice)
        {
            // Implementation for calculating item discounts
            // This is a placeholder
        }

        private static void ExtractPurchaseDate(string formattedText, ref Invoice invoice)
        {
            var dateMatch = Regex.Match(formattedText, @"Date\s*:\s*(\d{2}-[A-Za-z]{3}-\d{4})");
            if (dateMatch.Success)
            {
                invoice.PurchaseDate = dateMatch.Groups[1].Value.Trim();
            }
        }

        private static void ExtractPurchaseTime(string formattedText, ref Invoice invoice)
        {
            var timeMatch = Regex.Match(formattedText, @"Inv Time\s*:\s*(\d{1,2}:\d{2}\s*[AP]M)");
            if (timeMatch.Success)
            {
                invoice.PurchaseDate = $"{invoice.PurchaseDate} {timeMatch.Groups[1].Value.Trim()}";
            }
        }
    }
}`,
    },
    {
      id: "coffee-shop",
      name: "Coffee Shop Strategy",
      description:
        "Parse invoices from coffee shops with product names, quantities and prices.",
      icon: <Coffee className="h-10 w-10 text-brown-500" />,
      code: `using DigiReceiptOcr.Models;
using System;
using System.Collections.Generic;
using System.Text.RegularExpressions;

namespace DigiReceiptOcr.Strategies
{
    public class CoffeeShopParsingStrategy : IInvoiceParsingStrategy
    {
        public void ExtractInvoiceDetails(string formattedText, ref Invoice invoice, InvoiceTemplateConfig config)
        {
            // Set template name if not already set
            if (string.IsNullOrEmpty(invoice.TemplateName))
            {
                invoice.TemplateName = "CoffeeShop";
            }

            ExtractShopInfo(formattedText, ref invoice);
            ExtractInvoiceNumber(formattedText, ref invoice);
            ExtractDateAndTime(formattedText, ref invoice);
            ExtractItems(formattedText, ref invoice);
            ExtractTotals(formattedText, ref invoice);
        }

        private void ExtractShopInfo(string formattedText, ref Invoice invoice)
        {
            // Extract shop name from the first line
            var lines = formattedText.Split(new[] { '\\n', '\\r' }, StringSplitOptions.RemoveEmptyEntries);
            if (lines.Length > 0)
            {
                invoice.ShopName = lines[0].Trim();
            }

            // Extract shop address - usually in the next few lines
            var addressLines = new List<string>();
            for (int i = 1; i < Math.Min(5, lines.Length); i++)
            {
                var line = lines[i].Trim();
                if (!string.IsNullOrWhiteSpace(line) && 
                    !Regex.IsMatch(line, @"(?:invoice|receipt|bill|tel|phone|email)", RegexOptions.IgnoreCase))
                {
                    addressLines.Add(line);
                }
                else if (addressLines.Count > 0)
                {
                    break; // Stop after the first non-address line after finding address lines
                }
            }
            invoice.ShopAddress = string.Join(", ", addressLines);

            // Extract phone number
            var phoneMatch = Regex.Match(formattedText, @"(?:Tel|Phone|Contact)[\s:]*([0-9+\s-]{7,15})", RegexOptions.IgnoreCase);
            if (phoneMatch.Success)
            {
                invoice.ShopMobileNumber = phoneMatch.Groups[1].Value.Trim();
            }
        }

        private void ExtractInvoiceNumber(string formattedText, ref Invoice invoice)
        {
            // Try different patterns for invoice numbers
            var patterns = new[]
            {
                @"(?:Invoice|Receipt|Bill)\\s*#?\\s*[:.]?\\s*(\\d+)",
                @"#\\s*(\\d+)",
                @"Order\\s*[:.]?\\s*(\\d+)"
            };

            foreach (var pattern in patterns)
            {
                var match = Regex.Match(formattedText, pattern, RegexOptions.IgnoreCase);
                if (match.Success)
                {
                    invoice.InvoiceNo = match.Groups[1].Value.Trim();
                    return;
                }
            }
        }

        private void ExtractDateAndTime(string formattedText, ref Invoice invoice)
        {
            // Extract date in various formats
            var datePatterns = new[]
            {
                @"Date\\s*[:.]?\\s*(\\d{1,2}[/.-]\\d{1,2}[/.-]\\d{2,4})",
                @"Date\\s*[:.]?\\s*(\\d{1,2}\\s+[A-Za-z]{3,}\\s+\\d{2,4})"
            };

            foreach (var pattern in datePatterns)
            {
                var match = Regex.Match(formattedText, pattern, RegexOptions.IgnoreCase);
                if (match.Success)
                {
                    invoice.PurchaseDate = match.Groups[1].Value.Trim();
                    break;
                }
            }

            // Extract time
            var timeMatch = Regex.Match(formattedText, @"Time\\s*[:.]?\\s*(\\d{1,2}:\\d{2}\\s*(?:AM|PM)?)", RegexOptions.IgnoreCase);
            if (timeMatch.Success)
            {
                // Append time to date if date exists
                if (!string.IsNullOrEmpty(invoice.PurchaseDate))
                {
                    invoice.PurchaseDate += " " + timeMatch.Groups[1].Value.Trim();
                }
                else
                {
                    invoice.PurchaseDate = timeMatch.Groups[1].Value.Trim();
                }
            }
        }

        private void ExtractItems(string formattedText, ref Invoice invoice)
        {
            // Implementation for extracting coffee items
            // This is a simplified implementation
            var items = new List<Item>();
            var itemPattern = @"(\\d+)\\s+([\\w\\s]+)\\s+(\\d+\\.\\d{2})";
            
            var matches = Regex.Matches(formattedText, itemPattern);
            
            foreach (Match match in matches)
            {
                if (match.Groups.Count >= 4)
                {
                    var quantity = decimal.Parse(match.Groups[1].Value);
                    var description = match.Groups[2].Value.Trim();
                    var price = decimal.Parse(match.Groups[3].Value);
                    
                    items.Add(new Item
                    {
                        Description = description,
                        Quantity = quantity,
                        Rate = price / quantity,
                        MRP = price / quantity,  // Assuming MRP same as rate for coffee shops
                        SubTotal = price,
                        Discount = 0  // No discount in basic implementation
                    });
                }
            }
            
            invoice.ItemsList = items;
        }

        private void ExtractTotals(string formattedText, ref Invoice invoice)
        {
            // Extract subtotal
            var subTotalMatch = Regex.Match(formattedText, @"(?:Subtotal|Sub-total|Sub total)\\s*[:.]?\\s*(\\d+\\.\\d{2})", RegexOptions.IgnoreCase);
            if (subTotalMatch.Success)
            {
                invoice.SubTotal = decimal.Parse(subTotalMatch.Groups[1].Value);
            }
            else if (invoice.ItemsList != null && invoice.ItemsList.Count > 0)
            {
                // Calculate subtotal from items if not found directly
                invoice.SubTotal = invoice.ItemsList.Sum(i => i.SubTotal);
            }

            // Extract tax
            var taxMatch = Regex.Match(formattedText, @"(?:Tax|VAT|GST)\\s*[:.]?\\s*(\\d+\\.\\d{2})", RegexOptions.IgnoreCase);
            if (taxMatch.Success)
            {
                // We might want to add a Tax property to the Invoice model
                // For now, we'll just incorporate it into the calculation
                var tax = decimal.Parse(taxMatch.Groups[1].Value);
                
                // Adjust subtotal if needed
                if (invoice.SubTotal > 0)
                {
                    invoice.SubTotal -= tax;
                }
            }

            // Extract grand total
            var totalMatch = Regex.Match(formattedText, @"(?:Total|Grand Total|Amount|Bill Amount)\\s*[:.]?\\s*(\\d+\\.\\d{2})", RegexOptions.IgnoreCase);
            if (totalMatch.Success)
            {
                invoice.GrandTotal = decimal.Parse(totalMatch.Groups[1].Value);
            }
            else if (invoice.SubTotal > 0)
            {
                // Use subtotal as grand total if not found directly
                invoice.GrandTotal = invoice.SubTotal;
            }

            // No specific extraction for discount in this implementation
            invoice.TotalDiscount = 0;
        }
    }
}`,
    },
    {
      id: "template-skeleton",
      name: "Template Skeleton",
      description:
        "A basic skeleton for creating new invoice parsing strategies.",
      icon: <FileCheck className="h-10 w-10 text-gray-500" />,
      code: `using DigiReceiptOcr.Models;
using System;
using System.Collections.Generic;
using System.Text.RegularExpressions;

namespace DigiReceiptOcr.Strategies
{
    public class CustomParsingStrategy : IInvoiceParsingStrategy
    {
        public void ExtractInvoiceDetails(string formattedText, ref Invoice invoice, InvoiceTemplateConfig config)
        {
            // Set template name
            invoice.TemplateName = "CustomTemplate";

            // Extract basic invoice information
            ExtractShopInformation(formattedText, ref invoice);
            ExtractInvoiceNumber(formattedText, ref invoice);
            ExtractDateAndTime(formattedText, ref invoice);
            
            // Extract items and financial information
            ExtractItems(formattedText, ref invoice);
            ExtractTotals(formattedText, ref invoice);
        }

        private void ExtractShopInformation(string formattedText, ref Invoice invoice)
        {
            // TODO: Extract shop name, address, and contact info
            var lines = formattedText.Split('\\n');
            
            // Example implementation - customize based on your receipt format
            if (lines.Length > 0)
            {
                invoice.ShopName = lines[0].Trim();
            }
            
            // Example address extraction
            // invoice.ShopAddress = "...";
            
            // Example phone extraction
            // var phoneMatch = Regex.Match(formattedText, @"Phone:\s*(\d+)");
            // if (phoneMatch.Success)
            // {
            //     invoice.ShopMobileNumber = phoneMatch.Groups[1].Value;
            // }
        }

        private void ExtractInvoiceNumber(string formattedText, ref Invoice invoice)
        {
            // TODO: Extract invoice number using regex
            var invoiceMatch = Regex.Match(formattedText, @"Invoice #:\s*(\d+)");
            if (invoiceMatch.Success)
            {
                invoice.InvoiceNo = invoiceMatch.Groups[1].Value;
            }
        }

        private void ExtractDateAndTime(string formattedText, ref Invoice invoice)
        {
            // TODO: Extract date and time information
            var dateMatch = Regex.Match(formattedText, @"Date:\s*(\d{2}/\d{2}/\d{4})");
            if (dateMatch.Success)
            {
                invoice.PurchaseDate = dateMatch.Groups[1].Value;
            }
            
            // Add time if available
            var timeMatch = Regex.Match(formattedText, @"Time:\s*(\d{2}:\d{2})");
            if (timeMatch.Success)
            {
                invoice.PurchaseDate += " " + timeMatch.Groups[1].Value;
            }
        }

        private void ExtractItems(string formattedText, ref Invoice invoice)
        {
            // TODO: Extract item list from receipt
            var items = new List<Item>();
            
            // Example item extraction pattern - customize based on your receipt format
            // var itemPattern = @"(\d+)\s+([\w\s]+)\s+(\d+\.\d{2})\s+(\d+\.\d{2})";
            // var matches = Regex.Matches(formattedText, itemPattern);
            
            // foreach (Match match in matches)
            // {
            //     items.Add(new Item
            //     {
            //         Description = match.Groups[2].Value.Trim(),
            //         Quantity = decimal.Parse(match.Groups[1].Value),
            //         Rate = decimal.Parse(match.Groups[3].Value),
            //         SubTotal = decimal.Parse(match.Groups[4].Value),
            //         MRP = decimal.Parse(match.Groups[3].Value), // Assuming MRP same as rate
            //     });
            // }
            
            invoice.ItemsList = items;
        }

        private void ExtractTotals(string formattedText, ref Invoice invoice)
        {
            // TODO: Extract subtotal, discount, taxes, and grand total
            
            // Example subtotal extraction
            var subtotalMatch = Regex.Match(formattedText, @"Subtotal:\s*(\d+\.\d{2})");
            if (subtotalMatch.Success)
            {
                invoice.SubTotal = decimal.Parse(subtotalMatch.Groups[1].Value);
            }
            
            // Example discount extraction
            var discountMatch = Regex.Match(formattedText, @"Discount:\s*(\d+\.\d{2})");
            if (discountMatch.Success)
            {
                invoice.TotalDiscount = decimal.Parse(discountMatch.Groups[1].Value);
            }
            
            // Example grand total extraction
            var totalMatch = Regex.Match(formattedText, @"Total:\s*(\d+\.\d{2})");
            if (totalMatch.Success)
            {
                invoice.GrandTotal = decimal.Parse(totalMatch.Groups[1].Value);
            }
        }
    }
}`,
    },
  ];

  useEffect(() => {
    setFilteredStrategies(
      searchTerm
        ? strategies.filter(
            (s) =>
              s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              s.description.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : strategies
    );
  }, [searchTerm]);

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="sm:max-w-3xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Choose a Sample Strategy</AlertDialogTitle>
          <AlertDialogDescription>
            Select a pre-defined strategy template to get started with invoice
            parsing.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="mb-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="overflow-y-auto max-h-96 space-y-4">
          {filteredStrategies.map((strategy) => (
            <div
              key={strategy.id}
              className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
              onClick={() => onSelect(strategy.code)}
            >
              <div className="flex">
                <div className="mr-4 flex-shrink-0">{strategy.icon}</div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-lg font-medium text-gray-900">
                    {strategy.name}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {strategy.description}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {filteredStrategies.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>No strategies found matching "{searchTerm}"</p>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default SampleStrategyModal;
